"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface HorizontalScrollEdges {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

/**
 * Tracks whether a horizontal scroll container has overflow on either edge.
 * Used to show scroll affordance fades.
 */
export function useHorizontalScrollEdges<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [edges, setEdges] = useState<HorizontalScrollEdges>({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateEdges = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    if (maxScrollLeft <= 1) {
      setEdges({ canScrollLeft: false, canScrollRight: false });
      return;
    }

    setEdges({
      canScrollLeft: el.scrollLeft > 1,
      canScrollRight: el.scrollLeft < maxScrollLeft - 1,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    updateEdges();

    el.addEventListener("scroll", updateEdges, { passive: true });
    const observer = new ResizeObserver(updateEdges);
    observer.observe(el);
    if (el.firstElementChild) {
      observer.observe(el.firstElementChild);
    }

    return () => {
      el.removeEventListener("scroll", updateEdges);
      observer.disconnect();
    };
  }, [updateEdges]);

  return { ref, ...edges, updateEdges };
}
